import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  getPublicProfile, 
  getSocialActivityFeed, 
  savePublicBio,
  uploadGalleryPhoto,
  saveGalleryPhotoUrls,
  deleteGalleryPhoto,
  uploadProfilePhoto,
  saveMemberProfile,
  getBostonPerformanceStats
} from '../../firebase';
import { loyaltyApi, LoyaltyTransaction } from '../../services/loyaltyApi';
import { membersApi } from '../../services/membersApi';

export const usePublicProfile = (userId: string | undefined) => {
  return useQuery({
    queryKey: ['profile', userId],
    queryFn: () => {
      if (!userId) throw new Error("No user ID");
      return getPublicProfile(userId);
    },
    enabled: !!userId,
    staleTime: 2 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
  });
};

export const useLoyaltySummary = (userId: string | undefined) => {
  return useQuery({
    queryKey: ['loyalty', userId],
    queryFn: () => {
      if (!userId) throw new Error("No user ID");
      return loyaltyApi.getLoyaltySummary(userId);
    },
    enabled: !!userId,
    staleTime: 5 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
  });
};

export const useLoyaltyTransactions = (userId: string | undefined) => {
  return useQuery<LoyaltyTransaction[]>({
    queryKey: ['loyalty', userId, 'transactions'],
    queryFn: () => {
      if (!userId) throw new Error("No user ID");
      return loyaltyApi.getLoyaltyTransactions(userId);
    },
    enabled: !!userId,
    staleTime: 3 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
  });
};

export const useBostonPerformanceStats = (options?: { enabled?: boolean }) => {
  return useQuery({
    queryKey: ['stats', 'bostons'],
    queryFn: () => getBostonPerformanceStats(),
    enabled: options?.enabled ?? true,
    staleTime: 15 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
  });
};

export const useSocialActivityFeed = (userId: string | undefined) => {
  return useQuery({
    queryKey: ['activityFeed', userId],
    queryFn: () => {
      if (!userId) throw new Error("No user ID");
      return getSocialActivityFeed(false, userId);
    },
    enabled: !!userId,
  });
};

export const useUpdateBio = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (bio: string) => {
      const result = await savePublicBio(bio);
      if (!result.success) throw new Error(result.message);
      return result;
    },
    onSuccess: () => {
      // Invalidate profiles to refresh data
      queryClient.invalidateQueries({ queryKey: ['profile'] });
    }
  });
};

import { clearFollowingCache } from '../../arena/state/ArenaFriendsService';

export const useUpdateProfilePhoto = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ file, name, type }: { file: string, name: string, type: string }) => {
      const result = await uploadProfilePhoto(name, type, file);
      if (!result.success) throw new Error(result.message);
      return result.message; // returns the new URL
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['profile'] });
      queryClient.invalidateQueries({ queryKey: ['members'] });
      queryClient.invalidateQueries({ queryKey: ['directory'] });
    }
  });
};

export const useUpdateGalleryPhoto = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ slot, file, name, type }: { slot: number, file: string, name: string, type: string }) => {
      const result = await uploadGalleryPhoto(slot, name, type, file);
      if (!result.success) throw new Error(result.message);
      return result.message; // returns the new URL
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['profile'] });
    }
  });
};

export const useSaveGalleryUrls = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (urls: string[]) => {
      await saveGalleryPhotoUrls(urls);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['profile'] });
    }
  });
};

export const useDeleteGalleryPhoto = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (slot: number) => {
      await deleteGalleryPhoto(slot);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['profile'] });
    }
  });
};

export const useUpdateMemberProfile = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (profileData: any) => {
      const result = await saveMemberProfile(profileData);
      if (!result.success) throw new Error(result.message);
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['profile'] });
      queryClient.invalidateQueries({ queryKey: ['members'] });
      queryClient.invalidateQueries({ queryKey: ['directory'] });
    }
  });
};

export const useFollowMember = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (memberId: string) => {
      const result = await membersApi.followMember(memberId);
      if (!result.success) throw new Error(result.message || "Failed to follow");
      return result;
    },
    onSuccess: (_, memberId) => {
      queryClient.invalidateQueries({ queryKey: ['profile', memberId] });
      queryClient.invalidateQueries({ queryKey: ['profile'] });
      queryClient.invalidateQueries({ queryKey: ['directory'] });
      clearFollowingCache();
    }
  });
};

export const useUnfollowMember = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (memberId: string) => {
      const result = await membersApi.unfollowMember(memberId);
      if (!result.success) throw new Error(result.message || "Failed to unfollow");
      return result;
    },
    onSuccess: (_, memberId) => {
      queryClient.invalidateQueries({ queryKey: ['profile', memberId] });
      queryClient.invalidateQueries({ queryKey: ['profile'] });
      queryClient.invalidateQueries({ queryKey: ['directory'] });
      clearFollowingCache();
    }
  });
};
